import { faPlus } from "@fortawesome/free-solid-svg-icons"
import { useState, type ReactNode } from "react"
import { ButtonIcon } from "../Button/Button"
import { GenericDropDown } from "../Dropdown/Dropdown"
import THEME from "../../configuration/Theme"
import Animate, { AnimationTypes } from "../Animate/Animate"

export interface ProductCompositionOption {
  id: number
  label: string
}

export interface ProductCompositionGroup {
  key: string
  label: string
  placeholder: string
  options: ProductCompositionOption[]
  selectDescriptionKey: string
  addDescriptionKey: string
}

export interface ProductCompositionItem {
  key: string
  name: string
  typeLabel: string
  quantity?: number
  price?: number
  iconUrl?: string
  fallbackIcon: string
}

interface Props {
  title: string
  description: string
  groups: ProductCompositionGroup[]
  items: ProductCompositionItem[]
  emptyLabel: string
  removeLabel: string
  quantityLabel: string
  unitPriceLabel?: string
  totalPriceLabel?: string
  disabled?: boolean
  showControls?: boolean
  summary?: ReactNode
  onAdd: (groupKey: string, productId: number) => void
  onRemove: (itemKey: string) => void
  onQuantityChange?: (itemKey: string, quantity: number) => void
}

export const ProductCompositionSelector: React.FC<Props> = ({
  title,
  description,
  groups,
  items,
  emptyLabel,
  removeLabel,
  quantityLabel,
  unitPriceLabel,
  totalPriceLabel,
  disabled = false,
  showControls = true,
  summary,
  onAdd,
  onRemove,
  onQuantityChange,
}) => {
  const [selectedByGroup, setSelectedByGroup] = useState<Record<string, string>>({})

  return (
    <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
      <div className="rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
        <div className="mb-3">
          <h3 className="font-black tracking-tight text-gray-800">{title}</h3>

          <p className="text-xs text-gray-500">{description}</p>
        </div>

        {showControls && (
          <div className="mb-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
            {groups.map((group) => {
              const selectedId = selectedByGroup[group.key] ?? "0"

              return (
                <div key={group.key} className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
                  <label className="mb-2 block text-xs font-bold text-slate-600">{group.label}</label>

                  <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1 overflow-hidden">
                      <GenericDropDown
                        data={[
                          { label: group.placeholder, value: "0" },
                          ...group.options.map((option) => ({ label: option.label, value: String(option.id) })),
                        ]}
                        disable={disabled}
                        initialState={selectedId}
                        handlerFuncion={(event) =>
                          setSelectedByGroup((current) => ({ ...current, [group.key]: event.target.value }))
                        }
                        descriptionKey={group.selectDescriptionKey}
                      />
                    </div>

                    {selectedId !== "0" && (
                      <ButtonIcon
                        sizeNumber={38}
                        icon={faPlus}
                        disabled={disabled}
                        bgColor={THEME.colors.buttonBGPlusColor}
                        iconColor={THEME.colors.buttonFGPlusColor}
                        onClick={() => {
                          const productId = Number(selectedId)
                          if (productId <= 0) return

                          onAdd(group.key, productId)
                          setSelectedByGroup((current) => ({ ...current, [group.key]: "0" }))
                        }}
                        descriptionKey={group.addDescriptionKey}
                      />
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <div className="space-y-2">
          {summary}

          {items.length === 0 && (
            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-4 text-center text-sm text-slate-500">
              {emptyLabel}
            </div>
          )}

          {items.map((item) => {
            const quantity = item.quantity ?? 1
            const itemTotal = (item.price ?? 0) * quantity

            return (
              <div
                key={item.key}
                className="rounded-lg border border-slate-200 bg-white p-3 shadow-[0_2px_8px_rgba(0,0,0,0.03)]"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
                      {item.iconUrl ? (
                        <img src={item.iconUrl} alt={item.name} className="h-full w-full object-cover" />
                      ) : (
                        <span className="text-xl">{item.fallbackIcon}</span>
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-slate-700">{item.name}</p>

                      <p className="text-[11px] text-slate-500">{item.typeLabel}</p>

                      {item.price !== undefined && (
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <span className="text-[11px] font-bold text-slate-600">
                            {unitPriceLabel}{" "}
                            {item.price.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                          </span>

                          {onQuantityChange && (
                            <span className="text-[11px] font-black text-orange-600">
                              {totalPriceLabel}{" "}
                              {itemTotal.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {showControls && (
                    <div className="flex w-full flex-col items-stretch gap-2 sm:w-auto sm:flex-row sm:items-center">
                      {onQuantityChange && (
                        <div className="flex items-center justify-between gap-2 sm:justify-start">
                          <span className="text-xs font-semibold text-slate-500">{quantityLabel}</span>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              disabled={disabled}
                              className="h-8 w-8 rounded-md border border-slate-300 bg-slate-50 font-black text-slate-700 disabled:opacity-50"
                              onClick={() => onQuantityChange(item.key, Math.max(1, quantity - 1))}
                              data-interaction-description-key="interactionDescriptions:components.productCompositionSelector.decreaseQuantity"
                            >
                              −
                            </button>

                            <span className="flex h-8 w-10 items-center justify-center rounded-md border border-slate-300 bg-white text-sm font-black">
                              {quantity}
                            </span>

                            <button
                              type="button"
                              disabled={disabled}
                              className="h-8 w-8 rounded-md border border-orange-300 bg-orange-50 font-black text-orange-700 disabled:opacity-50"
                              onClick={() => onQuantityChange(item.key, quantity + 1)}
                              data-interaction-description-key="interactionDescriptions:components.productCompositionSelector.increaseQuantity"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      )}

                      <button
                        type="button"
                        disabled={disabled}
                        className="h-10 w-full rounded-lg border border-red-200 bg-red-50 px-3 text-sm font-bold text-red-600 transition-all hover:bg-red-100 disabled:opacity-50 sm:w-auto"
                        onClick={() => onRemove(item.key)}
                        data-interaction-description-key="interactionDescriptions:components.productCompositionSelector.remove"
                      >
                        {removeLabel}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </Animate>
  )
}
